import {
  ActivityCategory,
  onActivityDropped,
  trackActivity,
  type PortalActivityEvent,
} from "@/services/activity/portalActivityTracker";

export const CmsContentType = {
  carousel: "Carousel",
  loginCarousel: "LoginCarousel",
  news: "News",
} as const;

export type CmsContentTypeValue =
  (typeof CmsContentType)[keyof typeof CmsContentType];

const SEEN_STORAGE_KEY = "portalCmsImpressions";
const IMPRESSION = "Impression";
const CLICK = "Click";

// The CMS id when the co-op's CMS supplies one, otherwise what the item links to, and
// for an item that only opens a dialog, its headline.
export function getCmsContentKey(item: {
  contentItemId?: string;
  link?: string;
  text?: string;
}): string {
  return (item.contentItemId || item.link || item.text || "").trim().slice(0, 100);
}

function readSeen(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_STORAGE_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
}

function writeSeen(seen: Set<string>) {
  try {
    sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // a full or blocked sessionStorage only means the impression may be counted twice
  }
}

function seenKeyFor(action: string, type: string, key: string): string {
  return action === IMPRESSION ? `${type}|${key}` : `${action}|${type}|${key}`;
}

// Seen only once the event is queued; one the server refuses is forgotten, so the next
// time counts it.
function trackOnce(action: string, type: CmsContentTypeValue, key: string) {
  const seen = readSeen();
  const seenKey = seenKeyFor(action, type, key);

  if (seen.has(seenKey)) {
    return;
  }

  seen.add(seenKey);
  writeSeen(seen);

  if (!trackActivity(ActivityCategory.cms, action, key, type)) {
    forgetSeen([seenKey]);
  }
}

// Counted once per browser session however often a slide rotates past, so a
// click-through rate compares clicks with growers who saw the item, not with rotations.
export function trackCmsImpression(type: CmsContentTypeValue, key: string) {
  if (!key) {
    return;
  }

  trackOnce(IMPRESSION, type, key);
}

function forgetSeen(seenKeys: string[]) {
  const seen = readSeen();

  if (seenKeys.filter((x) => seen.delete(x)).length > 0) {
    writeSeen(seen);
  }
}

function forgetDropped(events: PortalActivityEvent[]) {
  forgetSeen(
    events
      .filter((x) => x.category === ActivityCategory.cms)
      .map((x) => seenKeyFor(x.action, x.reference, x.target))
  );
}

onActivityDropped(forgetDropped);

export function forgetCmsImpressions() {
  try {
    sessionStorage.removeItem(SEEN_STORAGE_KEY);
  } catch {
    // nothing stored to forget
  }
}

// Once per session, as impressions are, so clicks never outnumber them. A click on an
// item whose impression this session has forgotten, such as after a sign in, counts the
// impression too.
export function trackCmsClick(type: CmsContentTypeValue, key: string) {
  if (!key) {
    return;
  }

  trackOnce(IMPRESSION, type, key);
  trackOnce(CLICK, type, key);
}
